/* global axios */
import appConfig from '../../../shared/helpers/appConfig';
import { getCentralHeaders } from 'dashboard/helpers/centralApiHeaders';
import {
  getMockCampaign,
  getMockCampaigns,
  getMockQuota,
  getMockRecipients,
  isMockCampaignId,
  MOCK_ERROR_MESSAGES,
  mockCancel,
  mockContinue,
  mockShouldFail,
  mockUpdate,
  mockUpdateRecipients,
  touchMockCampaign,
} from './whatsappCampaignsMock';

// Development mock campaigns (see whatsappCampaignsMock.js). Only active when
// VUE_APP_ENV=development; they never call the backend.
const isDevMock = id =>
  appConfig('VUE_APP_ENV') === 'development' && isMockCampaignId(id);

const knowledgeProxy = (accountId, path) =>
  `/api/v1/accounts/${accountId}/proxy/knowledge${path}`;

// Used by continueTemplateCampaign/getWhatsappBroadcastQuota, which need raw
// fetch (not the axios wrapper knowledgeProxy's callers use) to read the
// response status directly for 409 (quota exhausted) handling.
const knowledgeProxyBase = accountId =>
  `/api/v1/accounts/${accountId}/proxy/knowledge`;

export const state = {
  templateCampaignList: [],
  templateCampaign: null,
  templateCampaignRecipients: [],
  isLoadingCampaigns: false,
  campaignError: null,
  selectedIds: [],
};

export const getters = {
  getTemplateCampaignList: state => state.templateCampaignList,
  getTemplateCampaign: state => state.templateCampaign,
  getTemplateCampaignRecipients: state => state.templateCampaignRecipients,
  isCampaignLoading: state => state.isLoadingCampaigns,
  getCampaignError: state => state.campaignError,
  getSelectedIds: state => state.selectedIds,
};

export const mutations = {
  setTemplateCampaignList(state, campaigns) { state.templateCampaignList = campaigns; },
  setTemplateCampaign(state, campaign) { state.templateCampaign = campaign; },
  setTemplateCampaignRecipients(state, recipients) { state.templateCampaignRecipients = recipients; },
  setIsLoadingCampaigns(state, isLoading) { state.isLoadingCampaigns = isLoading; },
  setCampaignError(state, error) { state.campaignError = error; },
  clearTemplateCampaign(state) { state.templateCampaign = null; },
  clearTemplateCampaignList(state) { state.templateCampaignList = []; },
  clearCampaignError(state) { state.campaignError = null; },
  SET_SELECTED_IDS(state, ids) { state.selectedIds = ids; },
  CLEAR_SELECTED_IDS(state) { state.selectedIds = []; },
  REMOVE_SELECTED_IDS(state, idsToRemove) {
    state.selectedIds = state.selectedIds.filter(id => !idsToRemove.includes(id));
  },
};

export const actions = {
  async fetchTemplateCampaigns(
    { rootGetters, commit },
    {
      inbox_id = '', status = '', name = '', template_name = '',
      num_recipients_min = '', num_recipients_max = '', created_at = '',
      search_options = '', q = '', page = 1, page_size = 100,
    } = {}
  ) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      const params = [
        `page=${page}`, `page_size=${page_size}`,
        status ? `status=${encodeURIComponent(status)}` : '',
        inbox_id ? `inbox_id=${encodeURIComponent(inbox_id)}` : '',
        name ? `name=${encodeURIComponent(name)}` : '',
        template_name ? `template_name=${encodeURIComponent(template_name)}` : '',
        num_recipients_min !== '' ? `num_recipients_min=${encodeURIComponent(num_recipients_min)}` : '',
        num_recipients_max !== '' ? `num_recipients_max=${encodeURIComponent(num_recipients_max)}` : '',
        created_at ? `created_at=${encodeURIComponent(created_at)}` : '',
        search_options ? `search_options=${encodeURIComponent(search_options)}` : '',
        q ? `q=${encodeURIComponent(q)}` : '',
      ].filter(Boolean).join('&');

      const { data: response } = await axios.get(
        knowledgeProxy(accountId, `/accounts/${accountId}/template_campaigns?${params}`)
      );
      
      // Development mock campaigns, one per status (see whatsappCampaignsMock.js)
      if (appConfig('VUE_APP_ENV') === 'development' && page === 1) {
        const mocks = getMockCampaigns({
          status, inbox_id, name, template_name,
          num_recipients_min, num_recipients_max, created_at, q,
        });
        if (mocks.length) {
          response.results = [...mocks, ...(response.results || [])];
          if (response.results.length > page_size) response.results = response.results.slice(0, page_size);
          response.total = (response.total || 0) + mocks.length;
        }
      }

      commit('setTemplateCampaignList', response);
    } catch (err) {
      throw err;
    }
  },

  async fetchTemplateCampaign({ rootGetters, commit }, { inboxId, campaignId }) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      if (isDevMock(campaignId)) {
        if (mockShouldFail('refresh')) throw new Error('Failed to refresh campaign status');
        touchMockCampaign(campaignId);
        const mockCampaign = getMockCampaign(campaignId);
        commit('setTemplateCampaign', mockCampaign);
        return mockCampaign;
      }

      const { data: response } = await axios.get(
        knowledgeProxy(accountId, `/accounts/${accountId}/inboxes/${inboxId}/template_campaigns/${campaignId}`)
      );
      commit('setTemplateCampaign', response);
      return response;
    } catch (err) {
      throw err;
    }
  },

  async fetchTemplateCampaignRecipients(
    { rootGetters, commit },
    { inboxId, campaignId, status = '', error_message = '', page = 1, page_size = 100 } = {}
  ) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      if (isDevMock(campaignId)) {
        const mockRecipients = getMockRecipients(campaignId, { status, error_message, page, page_size });
        commit('setTemplateCampaignRecipients', mockRecipients);
        return mockRecipients;
      }

      const query = `?page=${page}&page_size=${page_size}${status ? `&status=${status}` : ''}${error_message ? `&error_message=${encodeURIComponent(error_message)}` : ''}`;
      const { data: response } = await axios.get(
        knowledgeProxy(accountId, `/accounts/${accountId}/inboxes/${inboxId}/template_campaigns/${campaignId}/recipients${query}`)
      );
      commit('setTemplateCampaignRecipients', response);
      return response;
    } catch (err) {
      throw err;
    }
  },

  async createTemplateCampaign({ rootGetters }, { inboxId, payload }) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      const { data: json } = await axios.post(
        knowledgeProxy(accountId, `/accounts/${accountId}/inboxes/${inboxId}/template_campaigns`),
        payload
      );
      return json;
    } catch (err) {
      throw err;
    }
  },

  async rerunTemplateCampaign({ rootGetters }, { inboxId, campaignId, payload }) {
    const accountId = rootGetters.getCurrentAccountId;
    if (isDevMock(campaignId)) {
      return { success: true };
    }
    try {
      const { data: json } = await axios.post(
        knowledgeProxy(accountId, `/accounts/${accountId}/inboxes/${inboxId}/template_campaigns/${campaignId}/rerun`),
        payload
      );
      return json;
    } catch (err) {
      throw new Error("Error rerunning template campaign");
    }
  },

  async cancelTemplateCampaign({ rootGetters }, { inboxId, campaignId }) {
    const accountId = rootGetters.getCurrentAccountId;
    if (isDevMock(campaignId)) {
      if (mockShouldFail('cancel')) throw new Error('Error cancelling template campaign');
      return mockCancel(campaignId);
    }
    try {
      const { data: json } = await axios.post(
        knowledgeProxy(accountId, `/accounts/${accountId}/inboxes/${inboxId}/template_campaigns/${campaignId}/cancel`)
      );
      return json;
    } catch (err) {
      throw new Error("Error cancelling template campaign");
    }
  },

  async continueTemplateCampaign({ rootGetters }, { inboxId, campaignId }) {
    const accountId = rootGetters.getCurrentAccountId;

    if (isDevMock(campaignId)) {
      if (mockShouldFail('continue')) throw new Error('Failed to continue template campaign');
      return mockContinue(campaignId);
    }

    try {
      const rawResponse = await fetch(`${knowledgeProxyBase(accountId)}/accounts/${accountId}/inboxes/${inboxId}/template_campaigns/${campaignId}/continue`, {
        method: 'POST',
        headers: {
          ...getCentralHeaders(),
        },
      });

      if (!rawResponse.ok) {
        // 409 means today's quota is already spent — surface the server's
        // {message, resets_at, timezone} rather than a generic failure.
        const errorBody = await rawResponse.json().catch(() => null);
        const detail = errorBody && errorBody.detail;
        if (rawResponse.status === 409 && detail) {
          const err = new Error(detail.message || 'No broadcast quota left today');
          err.resetsAt = detail.resets_at;
          throw err;
        }
        throw new Error('Failed to continue template campaign');
      }

      const json = await rawResponse.json();
      return json;
    } catch (err) {
      throw err instanceof Error ? err : new Error('Error continuing template campaign');
    }
  },

  async getWhatsappBroadcastQuota({ rootGetters }) {
    const accountId = rootGetters.getCurrentAccountId;

    if (appConfig('VUE_APP_ENV') === 'development') {
      const mockQuota = getMockQuota();
      if (mockQuota) return mockQuota;
    }

    try {
      const rawResponse = await fetch(`${knowledgeProxyBase(accountId)}/accounts/${accountId}/whatsapp_broadcast_quota`, {
        method: 'GET',
        headers: {
          ...getCentralHeaders(),
        },
      });

      if (!rawResponse.ok) {
        throw new Error('Failed to fetch WhatsApp broadcast quota');
      }

      return await rawResponse.json();
    } catch (err) {
      throw new Error('Error fetching WhatsApp broadcast quota');
    }
  },

  async updateTemplateCampaign({ rootGetters }, { inboxId, campaignId, payload }) {
    const accountId = rootGetters.getCurrentAccountId;
    if (isDevMock(campaignId)) {
      if (mockShouldFail('update')) throw new Error('Error updating template campaign');
      return mockUpdate(campaignId, payload);
    }
    try {
      const { data: json } = await axios.put(
        knowledgeProxy(accountId, `/accounts/${accountId}/inboxes/${inboxId}/template_campaigns/${campaignId}`),
        payload
      );
      return json;
    } catch (err) {
      throw new Error('Error updating template campaign');
    }
  },

  async updateTemplateCampaignRecipients({ rootGetters }, { inboxId, campaignId, payload }) {
    const accountId = rootGetters.getCurrentAccountId;
    if (isDevMock(campaignId)) {
      if (mockShouldFail('update')) throw new Error('Error updating template campaign');
      return mockUpdateRecipients(campaignId, payload);
    }
    try {
      const { data: json } = await axios.put(
        knowledgeProxy(accountId, `/accounts/${accountId}/inboxes/${inboxId}/template_campaigns/${campaignId}/recipients`),
        payload
      );
      return json;
    } catch (err) {
      throw err;
    }
  },

  async checkPendingCampaignsForInbox({ rootGetters }, { inboxId }) {
    const accountId = rootGetters.getCurrentAccountId;
    const { data } = await axios.get(
      knowledgeProxy(accountId, `/accounts/${accountId}/template_campaigns?inbox_id=${encodeURIComponent(inboxId)}&status=pending&page_size=1`)
    );
    return (data.total || 0) > 0;
  },

  async deleteTemplateCampaign({ rootGetters }, { inboxId, campaignId }) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      await axios.delete(
        knowledgeProxy(accountId, `/accounts/${accountId}/inboxes/${inboxId}/template_campaigns/${campaignId}`)
      );
      return true;
    } catch (err) {
      throw new Error('Error deleting template campaign');
    }
  },

  async fetchRecipientErrorMessages({ rootGetters }, { inboxId, campaignId }) {
    if (isDevMock(campaignId)) {
      return { error_messages: MOCK_ERROR_MESSAGES };
    }

    const accountId = rootGetters.getCurrentAccountId;
    const { data } = await axios.get(
      knowledgeProxy(accountId, `/accounts/${accountId}/inboxes/${inboxId}/template_campaigns/${campaignId}/recipients/error_messages`)
    );
    return data;
  },

  async assignLabelToRecipients({ rootGetters }, { inboxId, campaignId, payload }) {
    if (isDevMock(campaignId)) {
      return { matched_contacts: 42, queued: true, task_id: 'mock-task-uuid' };
    }

    const accountId = rootGetters.getCurrentAccountId;
    const { data } = await axios.post(
      knowledgeProxy(accountId, `/accounts/${accountId}/inboxes/${inboxId}/template_campaigns/${campaignId}/recipients/assign_label`),
      payload
    );
    return data;
  },

  clearSelectedIds({ commit }) {
    commit('CLEAR_SELECTED_IDS');
  },

  updateSelectedIds({ commit, state }, ids) {
    const combined = Array.from(new Set([...state.selectedIds, ...ids]));
    commit('SET_SELECTED_IDS', combined);
  },

  deselectIds({ commit }, idsToRemove) {
    commit('REMOVE_SELECTED_IDS', idsToRemove);
  },
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
